import { css } from "dx-styles";
import { useEffect, useRef, useState } from "react";

import { api } from "../api/client";

import { FormulaField } from "../components/FormulaField";
import { button, errorText, muted, row, stack, subheading } from "../components/primitives";
import type { BundleItem, Parameter, ParameterValue, RulePredicate, TemplateRule } from "../lib/document";
import { newId } from "../lib/document";
import { ruleTypeLabel } from "../lib/ruleSummary";
import { tokens } from "../theme/tokens";
import { maxConditionalDepth } from "./RuleTree";

const field = css({
  minHeight: "34px",
  padding: `0 ${tokens.space.sm}`,
  borderRadius: tokens.radius.sm,
  border: `1px solid ${tokens.color.rule}`,
  backgroundColor: tokens.color.paper,
  width: "100%",
});

const grid = css({ display: "grid", gridTemplateColumns: "1fr 1fr", gap: tokens.space.sm });

const chip = css({
  border: `1px solid ${tokens.color.rule}`,
  backgroundColor: tokens.color.kraft,
  borderRadius: "999px",
  padding: `2px ${tokens.space.sm}`,
  fontSize: "12px",
});

const tagList = css({ display: "flex", gap: tokens.space.xs, flexWrap: "wrap" });

const operators: readonly RulePredicate["op"][] = ["==", "!=", ">", ">=", "<", "<="];

interface Props {
  readonly collectionId: string;
  readonly rule: TemplateRule;
  readonly items: readonly BundleItem[];
  readonly parameters: readonly Parameter[];
  readonly depth: number;
  readonly onChange: (rule: TemplateRule) => void;
  readonly onDelete: () => void;
}

/** Everything one rule can say, by type. */
export function RuleForm({ collectionId, rule, items, parameters, depth, onChange, onDelete }: Props) {
  const needsItem = rule.type === "include" || rule.type === "exclude" || rule.type === "override";

  return (
    <div className={stack}>
      <div className={row} style={{ justifyContent: "space-between" }}>
        <h2 className={subheading}>{ruleTypeLabel(rule.type)}</h2>
        <button type="button" className={button({ tone: "danger", size: "sm" })} onClick={onDelete}>
          Delete rule
        </button>
      </div>

      {needsItem ? (
        <label className={stack}>
          <span className={muted}>Item</span>
          <select
            className={field}
            value={rule.targetDefinitionID ?? ""}
            onChange={(event) => onChange({ ...rule, targetDefinitionID: event.target.value || null })}
          >
            <option value="">— pick an item —</option>
            {items.map((item) => (
              <option key={item.id} value={item.id}>
                {item.title} ({item.kind})
              </option>
            ))}
          </select>
          <span className={muted}>Items come from your library; add one there to see it here.</span>
        </label>
      ) : null}

      {rule.type === "include" || rule.type === "includeQuick" ? (
        <div className={stack}>
          <span className={muted}>Quantity</span>
          <FormulaField
            collectionId={collectionId}
            expression={rule.quantityExpression ?? ""}
            parameters={parameters}
            onChange={(expression) => onChange({ ...rule, quantityExpression: expression || null })}
          />
        </div>
      ) : null}

      {rule.type === "includeQuick" ? (
        <>
          <label className={stack}>
            <span className={muted}>Title</span>
            <input
              className={field}
              value={rule.quickTitle ?? ""}
              onChange={(event) => onChange({ ...rule, quickTitle: event.target.value || null })}
            />
            <span className={muted}>
              A quick item is not in your library: it exists only inside this template.
            </span>
          </label>
          <div className={grid}>
            <label className={stack}>
              <span className={muted}>Kind</span>
              <select
                className={field}
                value={rule.quickKind ?? "gear"}
                onChange={(event) =>
                  onChange({ ...rule, quickKind: event.target.value as NonNullable<TemplateRule["quickKind"]> })
                }
              >
                <option value="gear">gear</option>
                <option value="task">task</option>
                <option value="purchase">purchase</option>
              </select>
            </label>
            <label className={stack}>
              <span className={muted}>Notes</span>
              <input
                className={field}
                value={rule.quickNotes ?? ""}
                onChange={(event) => onChange({ ...rule, quickNotes: event.target.value || null })}
              />
            </label>
          </div>
        </>
      ) : null}

      {rule.type === "override" ? (
        <>
          <div className={grid}>
            <label className={stack}>
              <span className={muted}>Title</span>
              <input
                className={field}
                value={rule.overrideTitle ?? ""}
                onChange={(event) => onChange({ ...rule, overrideTitle: event.target.value || null })}
              />
            </label>
            <label className={stack}>
              <span className={muted}>Weight (kg)</span>
              <input
                className={field}
                value={rule.overrideWeight ?? ""}
                onChange={(event) =>
                  onChange({
                    ...rule,
                    overrideWeight: event.target.value === "" ? null : Number(event.target.value),
                  })
                }
              />
            </label>
          </div>
          <label className={stack}>
            <span className={muted}>Notes</span>
            <input
              className={field}
              value={rule.overrideNotes ?? ""}
              onChange={(event) => onChange({ ...rule, overrideNotes: event.target.value || null })}
            />
          </label>
          <label className={stack}>
            <span className={muted}>Tags (comma separated)</span>
            <input
              className={field}
              value={(rule.overrideTags ?? []).join(", ")}
              onChange={(event) =>
                onChange({
                  ...rule,
                  overrideTags:
                    event.target.value.trim() === ""
                      ? null
                      : event.target.value.split(",").map((tag) => tag.trim()).filter((tag) => tag !== ""),
                })
              }
            />
          </label>
          <span className={muted}>An override needs at least one of these to do anything.</span>
        </>
      ) : null}

      {rule.type === "includeGroup" || rule.type === "excludeGroup" ? (
        <GroupFields collectionId={collectionId} rule={rule} items={items} onChange={onChange} />
      ) : null}

      {rule.type === "conditional" ? (
        <ConditionalFields rule={rule} parameters={parameters} depth={depth} onChange={onChange} />
      ) : null}

      <label className={stack}>
        <span className={muted}>Note (for you, not published to subscribers&rsquo; checklists)</span>
        <input
          className={field}
          value={rule.note ?? ""}
          onChange={(event) => onChange({ ...rule, note: event.target.value || null })}
        />
      </label>
    </div>
  );
}

function GroupFields({
  collectionId,
  rule,
  items,
  onChange,
}: {
  readonly collectionId: string;
  readonly rule: TemplateRule;
  readonly items: readonly BundleItem[];
  readonly onChange: (rule: TemplateRule) => void;
}) {
  const known = [...new Set(items.flatMap((item) => item.tags.map((tag) => tag.toLowerCase())))].sort();
  const [matches, setMatches] = useState<readonly { id: string; title: string }[] | null>(null);
  const [checking, setChecking] = useState(false);
  const generation = useRef(0);
  const tags = rule.groupTags ?? [];
  const matchAll = rule.groupMatchAll ?? false;

  // Resolved by the engine, not by a second implementation of tag matching in
  // the browser: a group rule's whole effect is which items it picks up, and the
  // author has to see exactly what a subscriber will get.
  useEffect(() => {
    const mine = ++generation.current;
    if (tags.length === 0) {
      setMatches([]);
      return;
    }
    setChecking(true);
    const timer = window.setTimeout(() => {
      void (async () => {
        try {
          const response = await api.groupMatch(collectionId, tags, matchAll);
          if (generation.current === mine) setMatches(response.items);
        } catch {
          // Keep the last answer rather than flashing an empty set.
        } finally {
          if (generation.current === mine) setChecking(false);
        }
      })();
    }, 250);
    return () => window.clearTimeout(timer);
    // The tag list is compared by value, so a re-render with the same tags does
    // not re-query.
  }, [collectionId, tags.join("\u0000"), matchAll]);

  return (
    <>
      <label className={stack}>
        <span className={muted}>Tags (comma separated)</span>
        <input
          className={field}
          value={tags.join(", ")}
          onChange={(event) =>
            onChange({
              ...rule,
              groupTags: event.target.value
                .split(",")
                .map((tag) => tag.trim())
                .filter((tag) => tag !== ""),
            })
          }
          list="library-tags"
          autoComplete="off"
          spellCheck={false}
        />
        <datalist id="library-tags">
          {known.map((tag) => (
            <option key={tag} value={tag} />
          ))}
        </datalist>
        <span className={muted}>Tags are matched lower-cased, exactly as the engine normalizes them.</span>
      </label>
      <label className={row}>
        <input
          type="checkbox"
          checked={matchAll}
          onChange={(event) => onChange({ ...rule, groupMatchAll: event.target.checked })}
        />
        <span>Item must carry every tag (otherwise any one is enough)</span>
      </label>
      <div className={stack} aria-live="polite">
        <span className={muted}>
          {checking && matches === null
            ? "Checking what matches…"
            : (matches ?? []).length === 0
              ? "Nothing matches yet — this rule would do nothing."
              : `${matches?.length} item${matches?.length === 1 ? "" : "s"} match right now, resolved by the engine:`}
        </span>
        <div className={tagList}>
          {(matches ?? []).map((item) => (
            <span key={item.id} className={chip}>
              {item.title}
            </span>
          ))}
        </div>
        <span className={muted}>
          Group rules match at activation, so this set changes as your library grows — and every item in it
          travels in the published bundle.
        </span>
      </div>
    </>
  );
}

function ConditionalFields({
  rule,
  parameters,
  depth,
  onChange,
}: {
  readonly rule: TemplateRule;
  readonly parameters: readonly Parameter[];
  readonly depth: number;
  readonly onChange: (rule: TemplateRule) => void;
}) {
  const predicate = rule.predicate;
  const parameter = parameters.find(
    (candidate) => candidate.key.toLowerCase() === (predicate?.parameterKey ?? "").toLowerCase(),
  );

  const setPredicate = (next: Partial<RulePredicate>) => {
    const base: RulePredicate = predicate ?? { parameterKey: "", op: "==", value: { type: "int", value: 0 } };
    onChange({ ...rule, predicate: { ...base, ...next } });
  };

  /** The value editor follows the parameter's type, so a mismatch cannot be typed. */
  const valueFor = (raw: string): ParameterValue => {
    switch (parameter?.type) {
      case "boolean":
        return { type: "bool", value: raw === "true" };
      case "double":
        return { type: "double", value: Number(raw) };
      case "string":
        return { type: "string", value: raw };
      default:
        return { type: "int", value: Number.parseInt(raw, 10) || 0 };
    }
  };

  return (
    <>
      <div className={grid}>
        <label className={stack}>
          <span className={muted}>Parameter</span>
          <select
            className={field}
            value={predicate?.parameterKey ?? ""}
            onChange={(event) => setPredicate({ parameterKey: event.target.value })}
          >
            <option value="">— pick a parameter —</option>
            {parameters.map((candidate) => (
              <option key={candidate.id} value={candidate.key}>
                {candidate.key} ({candidate.type})
              </option>
            ))}
          </select>
        </label>
        <label className={stack}>
          <span className={muted}>Operator</span>
          <select
            className={field}
            value={predicate?.op ?? "=="}
            onChange={(event) => setPredicate({ op: event.target.value as RulePredicate["op"] })}
          >
            {operators.map((op) => (
              <option key={op} value={op}>
                {op}
              </option>
            ))}
          </select>
          {parameter && (parameter.type === "boolean" || parameter.type === "string") ? (
            <span className={muted}>Only == and != apply to this type.</span>
          ) : null}
        </label>
      </div>

      <label className={stack}>
        <span className={muted}>Value</span>
        {parameter?.type === "boolean" ? (
          <select
            className={field}
            value={String(predicate?.value.value ?? false)}
            onChange={(event) => setPredicate({ value: valueFor(event.target.value) })}
          >
            <option value="false">false</option>
            <option value="true">true</option>
          </select>
        ) : (
          <input
            className={field}
            value={String(predicate?.value.value ?? "")}
            onChange={(event) => setPredicate({ value: valueFor(event.target.value) })}
          />
        )}
      </label>

      <div className={stack}>
        <span className={muted}>
          Nested rules apply only while the condition holds.
        </span>
        {depth >= maxConditionalDepth ? (
          <span className={errorText}>
            This block is already two levels deep — add the rule to a parent instead.
          </span>
        ) : (
          <div className={row} style={{ flexWrap: "wrap" }}>
            {(["include", "exclude", "includeQuick", "includeGroup"] as const).map((type) => (
              <button
                key={type}
                type="button"
                className={button({ size: "sm" })}
                onClick={() =>
                  onChange({
                    ...rule,
                    conditionalRules: [...(rule.conditionalRules ?? []), { id: newId(), type }],
                  })
                }
              >
                + nested {type}
              </button>
            ))}
          </div>
        )}
      </div>
    </>
  );
}
